import { Divider } from "@octopusdeploy/design-system-components";
import type { ActionTemplateCategoryResource, ActionTemplateSearchResource, GitRefResource } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import { Environment } from "@octopusdeploy/utilities";
import * as React from "react";
import { InstalledActionTemplateList } from "~/areas/projects/components/Process/ActionTemplateSelector/InstalledActionTemplateList";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import type { ProcessIdentifier } from "~/areas/projects/components/Process/types";
import { isProcessOrProjectTemplateProcessIdentifier, isProjectTemplateProcessIdentifier, isProcessTemplateProcessIdentifier, isDeploymentOrRunbookProcessIdentifier, isDeploymentProcessIdentifier, isRunbookProcessIdentifier, isTemplatedProjectProcessIdentifier, } from "~/areas/projects/components/Process/types";
import { repository } from "~/clientInstance";
import { CommunityActionTemplateList } from "~/components/ActionTemplates/CommunityActionTemplateList";
import filterActionTemplates from "~/components/ActionTemplates/filterActionTemplates";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import { createSampleStepActionTemplateResource } from "~/components/Actions/sample/SampleStepPackage";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import FilterSearchBox from "~/components/FilterSearchBox/FilterSearchBox";
import Section from "~/components/Section";
import type { WithProcessContextInjectedProps } from "../Contexts/ProcessContext";
import { withProcessContext } from "../Contexts/ProcessContext";
import { withProcessQueryStringContext, type WithProcessQueryStringContextInjectedProps } from "../Contexts/ProcessQueryString/ProcessQueryStringContext";
import CategoryInfo from "./CategoryInfo";
import CategorySelector from "./CategorySelector";
import { ProcessTemplateActionTemplateList } from "./ProcessTemplateActionTemplateList";
import styles from "./style.module.less";
interface ActionTemplateSelectorState extends DataBaseComponentState {
    isLoaded: boolean;
    templates: ActionTemplateSearchResource[];
    categories: ActionTemplateCategoryResource[];
    childActionTypes: string[];
    filterText?: string;
    showCommunityTemplates: boolean;
    selectedCategory?: ActionHandlerCategory;
    communityStepCategories: ActionHandlerCategory[];
}
export type ActionTemplateSelectorProps = {
    parentStepId?: string;
    busy: boolean;
    errors: Error[];
    gitRefResource: GitRefResource | undefined;
    children?: React.ReactNode;
} & WithProcessContextInjectedProps & WithProcessQueryStringContextInjectedProps;
class ActionTemplateSelector extends DataBaseComponent<ActionTemplateSelectorProps, ActionTemplateSelectorState> {
    constructor(props: ActionTemplateSelectorProps) {
        super(props);
        this.state = {
            isLoaded: false,
            templates: [],
            categories: [],
            childActionTypes: [],
            showCommunityTemplates: false,
            selectedCategory: this.getDefaultCategory(),
            communityStepCategories: [
                ActionHandlerCategory.Aws,
                ActionHandlerCategory.Azure,
                ActionHandlerCategory.Certificate,
                ActionHandlerCategory.Docker,
                ActionHandlerCategory.GoogleCloud,
                ActionHandlerCategory.JavaAppServer,
                ActionHandlerCategory.Kubernetes,
                ActionHandlerCategory.Package,
                ActionHandlerCategory.Script,
                ActionHandlerCategory.Terraform,
                ActionHandlerCategory.WindowsServer,
            ],
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const actionsPlugins = pluginRegistry.getAllActions();
            const categories = await repository.ActionTemplates.categories();
            let templates = await fetchTemplates(this.props.processContext.state.processIdentifier);
            let updatedCommunityStepCategories = this.state.communityStepCategories;
            const childActionTypes = actionsPlugins.filter((action) => action.canBeChild).map((action) => action.actionType);
            if (this.state.selectedCategory) {
                templates = this.getTemplatesWithUpdatedCategories(templates, this.state.selectedCategory);
                updatedCommunityStepCategories = this.state.communityStepCategories.filter((x) => x != this.state.selectedCategory);
            }
            this.setState({
                isLoaded: true,
                categories: this.getAllowedCategories(categories),
                templates: templates,
                childActionTypes,
                communityStepCategories: updatedCommunityStepCategories,
            });
        });
    }
    render() {
        const communityTemplates = this.state.templates.filter((at) => !at.IsInstalled);
        const installedActionTemplates = this.installedActionTemplates();
        const processTemplateActionTemplates = this.processTemplateActionTemplates();
        return (<ProcessSubPageLayout title={<div className={styles.pageTitle}>Add Step</div>} busy={this.state.busy} errors={this.errors}>
                {this.state.isLoaded && (<div className={styles.container}>
                        <Section>
                            <FilterSearchBox placeholder="Filter by name, category or description..." value={this.state.filterText} onChange={this.filterChanged} fullWidth={true} containerClassName={styles.filterSearchBoxContainer} autoFocus={true} debounceDelay={500}/>
                        </Section>
                        <Divider />
                        <div className={styles.stepSelectionContainer}>
                            <div className={styles.categoriesContainer}>
                                <CategorySelector categories={this.state.categories} selectedCategory={this.state.selectedCategory} steps={this.state.templates} onCategorySelected={this.categoryChanged}/>
                            </div>
                            <Divider />
                            <div className={styles.stepsContainer}>
                                {this.state.selectedCategory ? <CategoryInfo categories={this.state.categories} selectedCategory={this.state.selectedCategory}/> : <></>}
                                {this.state.filterText ? <div className={styles.searchHeader}>Results for "{this.state.filterText}"</div> : <></>}
                                {(processTemplateActionTemplates.length > 0 || this.state.selectedCategory === ActionHandlerCategory.ProcessTemplate) && (<ProcessTemplateActionTemplateList key={`processTemplate-${this.state.selectedCategory}`} categories={this.state.categories} templates={processTemplateActionTemplates} onPostSelectionUrlRequested={this.getNewStepUrl} filter={this.state.filterText?.toLowerCase()} activeCategory={this.state.selectedCategory}/>)}
                                <InstalledActionTemplateList key={`installedAction-${this.state.selectedCategory}`} categories={this.state.categories} templates={installedActionTemplates} onDetailsUrlRequested={this.getDetailsUrl} onPostSelectionUrlRequested={this.getNewStepUrl} filter={this.state.filterText?.toLowerCase()} activeCategory={this.state.selectedCategory}/>
                                {this.state.selectedCategory !== ActionHandlerCategory.Featured && this.shouldShowCommunityTemplateList(this.state.selectedCategory) && (<CommunityActionTemplateList spaceId={isDeploymentOrRunbookProcessIdentifier(this.props.processContext.state.processIdentifier) ? this.props.processContext.state.processIdentifier.spaceId : null} templates={communityTemplates} filter={this.state.filterText?.toLowerCase()} installationActionName={isDeploymentOrRunbookProcessIdentifier(this.props.processContext.state.processIdentifier) ? "Install and Add Step" : "Add Step"} onDetailsUrlRequested={this.getDetailsUrl} onPostSelectionUrlRequested={this.getNewStepUrl} activeCategory={this.state.selectedCategory !== ActionHandlerCategory.Community ? this.state.selectedCategory : undefined} isProducerTemplateProcess={isProcessTemplateProcessIdentifier(this.props.processContext.state.processIdentifier)}/>)}
                            </div>
                        </div>
                    </div>)}
            </ProcessSubPageLayout>);
    }
    private shouldShowCommunityTemplateList(category: ActionHandlerCategory | undefined): boolean {
        if (this.state.filterText) {
            return true;
        }
        return !!category && category !== ActionHandlerCategory.BuiltInStep && category !== ActionHandlerCategory.StepTemplate && category !== ActionHandlerCategory.Other;
    }
    private installedActionTemplates(): ActionTemplateSearchResource[] {
        const installed = this.state.templates.filter((at) => at.IsInstalled && at.Type !== "Octopus.ProcessTemplate");
        const parentStepId = this.props.parentStepId;
        if (!parentStepId) {
            return installed;
        }
        return installed.filter((at) => this.state.childActionTypes.includes(at.Type));
    }
    private processTemplateActionTemplates(): ActionTemplateSearchResource[] {
        return this.state.templates.filter((at) => at.Type === "Octopus.ProcessTemplate");
    }
    private getDetailsUrl = (template: ActionTemplateSearchResource): {
        href: LinkHref;
        external: boolean;
    } => {
        const processIdentifier = this.props.processContext.state.processIdentifier;
        if (isDeploymentOrRunbookProcessIdentifier(processIdentifier)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return { href: links.communityStepTemplatePage.generateUrl({ spaceId: processIdentifier.spaceId, templateId: template.CommunityActionTemplateId! }), external: false };
        }
        else if (isProcessTemplateProcessIdentifier(processIdentifier)) {
            return { href: template.Website ?? "https://library.octopus.com", external: true };
        }
        else if (isTemplatedProjectProcessIdentifier(processIdentifier)) {
            return { href: template.Website ?? "https://library.octopus.com", external: true };
        }
        else if (isProjectTemplateProcessIdentifier(processIdentifier)) {
            return { href: template.Website ?? "https://library.octopus.com", external: true };
        }
        exhaustiveCheck(processIdentifier, "Unknown process type");
    };
    private getNewStepUrl = (template: {
        Type: string;
        Id: string | null;
        SpaceId: string | null;
        CommunityActionTemplateId: string | null;
    }): LinkHref => {
        const processIdentifier = this.props.processContext.state.processIdentifier;
        const gitRef = this.props.gitRefResource;
        if (isDeploymentProcessIdentifier(processIdentifier)) {
            return generateNewDeploymentProcessStepUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, gitRef, template, parentStepId: this.props.parentStepId });
        }
        if (isRunbookProcessIdentifier(processIdentifier)) {
            return links.runbookProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, runbookId: processIdentifier.runbookId, processId: processIdentifier.id }, { actionType: template.Type, new: true, parentStepId: this.props.parentStepId, templateId: template.Id ?? undefined });
        }
        if (isProcessTemplateProcessIdentifier(processIdentifier)) {
            return links.editProcessTemplatePage.generateUrl({ slug: processIdentifier.slug }, {
                gitRef: gitRef?.CanonicalName,
                actionType: template.Type,
                new: true,
                parentStepId: this.props.parentStepId,
                templateId: template.Id ?? undefined,
                templateSpaceId: template.SpaceId ?? "system",
                isCommunityTemplate: !!template.CommunityActionTemplateId,
            });
        }
        if (isTemplatedProjectProcessIdentifier(processIdentifier)) {
            throw new Error("Templated projects cannot add new steps.");
        }
        if (isProjectTemplateProcessIdentifier(processIdentifier)) {
            return links.projectTemplateDeploymentProcessEditorPage.generateUrl({ slug: processIdentifier.slug, gitRef: processIdentifier.gitRef }, { actionType: template.Type, new: true, parentStepId: this.props.parentStepId, templateId: template.Id ?? undefined, templateSpaceId: template.SpaceId ?? undefined });
        }
        exhaustiveCheck(processIdentifier, "Unknown process type");
    };
    private getDefaultCategory = (): ActionHandlerCategory => {
        const processIdentifier = this.props.processContext.state.processIdentifier;
        // Runbook
        if (isRunbookProcessIdentifier(processIdentifier)) {
            return ActionHandlerCategory.Script;
        }
        // Process
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const actionCategory = ActionHandlerCategory[this.props.processQueryStringContext.state.queryFilter.actionCategory as keyof typeof ActionHandlerCategory];
        return actionCategory ? actionCategory : ActionHandlerCategory.Featured;
    };
    private getAllowedCategories = (categories: ActionTemplateCategoryResource[]) => {
        const processIdentifier = this.props.processContext.state.processIdentifier;
        if (isRunbookProcessIdentifier(processIdentifier)) {
            return categories.filter((x) => x.Id !== ActionHandlerCategory.Featured);
        }
        if (isProcessTemplateProcessIdentifier(processIdentifier)) {
            return categories.filter((x) => x.Id !== ActionHandlerCategory.ProcessTemplate);
        }
        return categories;
    };
    private categoryChanged = (newCategory: ActionHandlerCategory | undefined) => {
        if (newCategory && this.state.communityStepCategories.some((x) => x === newCategory)) {
            const updatedTemplates = this.getTemplatesWithUpdatedCategories(this.state.templates, newCategory);
            const updatedCategoriesToFindInCommunitySteps = this.state.communityStepCategories.filter((x) => x != newCategory);
            this.setState({ filterText: undefined, selectedCategory: newCategory, templates: updatedTemplates, communityStepCategories: updatedCategoriesToFindInCommunitySteps });
        }
        else {
            this.setState({ filterText: undefined, selectedCategory: newCategory });
        }
    };
    private filterChanged = (newFilterText: string) => {
        const filterStr = newFilterText?.trim().replace(/  +/g, " ");
        this.setState({ filterText: filterStr ? filterStr : undefined, selectedCategory: filterStr ? undefined : this.getDefaultCategory() });
    };
    private getTemplatesWithUpdatedCategories(templates: ActionTemplateSearchResource[], newCategory: ActionHandlerCategory) {
        const communitySteps = templates.filter((x) => x.Categories.includes(ActionHandlerCategory.StepTemplate) && !x.Categories.includes(newCategory));
        const communityStepsToUpdateCategories = filterActionTemplates(communitySteps, newCategory.toLocaleLowerCase()).map((x) => x.Id);
        const updatedTemplates = templates.map((x) => (!communityStepsToUpdateCategories.includes(x.Id) ? x : { ...x, Categories: [...x.Categories, newCategory] }));
        return updatedTemplates;
    }
    static displayName = "ActionTemplateSelector";
}
interface GenerateNewDeploymentProcessStepUrlOptions {
    spaceId: string;
    projectSlug: string;
    parentStepId: string | undefined;
    gitRef: GitRefResource | undefined;
    template: {
        Type: string;
        Id: string | null;
        SpaceId: string | null;
    };
}
function generateNewDeploymentProcessStepUrl({ spaceId, projectSlug, parentStepId, gitRef, template }: GenerateNewDeploymentProcessStepUrlOptions) {
    if (gitRef) {
        return links.branchDeploymentProcessEditorPage.generateUrl({ spaceId, projectSlug, branchName: gitRef.CanonicalName }, { new: true, actionType: template.Type, parentStepId, templateId: template.Id ?? undefined });
    }
    return links.deploymentProcessEditorPage.generateUrl({ spaceId, projectSlug }, { new: true, actionType: template.Type, parentStepId, templateId: template.Id ?? undefined });
}
async function fetchTemplates(processIdentifier: ProcessIdentifier): Promise<ActionTemplateSearchResource[]> {
    const templates = isProcessOrProjectTemplateProcessIdentifier(processIdentifier)
        ? (await repository.forSystem().ActionTemplatesV2.search()).filter((t) => (t.CommunityActionTemplateId ? t.SpaceId === null : true))
        : await repository.ActionTemplatesV2.search();
    if (Environment.isInDevelopmentMode()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const inDevelopmentTemplates = [createSampleStepActionTemplateResource(repository.spaceId!)];
        const templateIds = new Set(templates.map((t) => t.Type));
        const inDevelopmentTemplatesThatArentReturnedByServer = inDevelopmentTemplates.filter((t) => !templateIds.has(t.Type));
        return [...templates, ...inDevelopmentTemplatesThatArentReturnedByServer];
    }
    return templates;
}
const EnhancedActionTemplateSelector = withProcessContext(withProcessQueryStringContext(ActionTemplateSelector));
export default EnhancedActionTemplateSelector;
